import React, { Fragment, useState, useEffect } from 'react';
import PageHeader from '../common/PageHeader';
import { useDataFetch } from '../../Hooks/Use-data-fetch';
import { useSettlementsSummary } from '../../Hooks/useSettlementsSummary';
import DashboardFilters from './DashboardFilters';
import DashboardStatCard from './DashboardStatCard';
import SalesGrowthChart from './SalesGrowthChart';
import UsersComparisonChart from './UsersComparisonChart';
import CriticalAlertsCard from './CriticalAlertsCard';
import AutomaticInsightsCard from './AutomaticInsightsCard';
import TopNetworksCard from './TopNetworksCard';
import { useSettlementData } from '../../Hooks/useSettlementData';
import { formatPeriodDate, formatPeriodMonth, formatPeriodRange } from '../../utils/date';

/**
 * Plantilla de las tarjetas. `value` y `growth` se sobrescriben abajo con los
 * datos reales según el título.
 *
 * `growth: null` significa "no hay con qué comparar" y la tarjeta lo dice
 * explícitamente. Antes había porcentajes escritos a mano (8.4, 9.6, 12.6,
 * 18.6) que se pintaban como si fueran medidos: se quitaron, porque un número
 * inventado en un tablero es peor que un espacio en blanco.
 */
const cards = [

  {
    title: "Regalías Totales",
    shortTitle: "Regalías",
    subtitle: "Tiempo Real",
    value: "COP 0",
    growth: null,
    growthLabel: "vs mes anterior",
    icon: "ri-money-dollar-circle-line",
    color: "#0F9A5A"
  },

  {
    title: "Bonos Totales",
    shortTitle: "Bonos",
    subtitle: "Tiempo Real",
    value: "COP 0",
    growth: null,
    growthLabel: "vs semana anterior",
    icon: "ri-gift-line",
    color: "#7C3AED"
  },

  {
    title: "Ventas Netas",
    shortTitle: "Ventas netas",
    subtitle: "Sin comisiones",
    value: "COP 0",
    growth: null,
    growthLabel: "vs mes anterior",
    icon: "ri-bar-chart-2-line",
    color: "#4B36F0"
  },

  {
    // Usuarios distintos que compraron este mes, contra los que compraron el
    // mes pasado. Un usuario con varias órdenes cuenta una sola vez, y las
    // órdenes creadas sin pagar o canceladas no cuentan.
    title: "Usuarios Activos",
    shortTitle: "Usuarios activos",
    subtitle: "Compraron este mes",
    value: "0",
    growth: null,
    growthLabel: "vs mes anterior",
    icon: "ri-team-line",
    color: "#4B36F0"
  },

  {
    // Según el diseño, aquí el número grande ES el porcentaje: cuánto varió la
    // afiliación de los últimos 3 meses contra los 3 anteriores. Por eso va en
    // el color del hexágono (accentValue) y sin variación debajo.
    title: "Crecimiento de Red",
    shortTitle: "Crecimiento de red",
    subtitle: "vs 3 meses anteriores",
    value: "—",
    growth: null,
    growthLabel: "",
    icon: "ri-share-line",
    color: "#D97706",
    accentValue: true
  }

];


const Ecommerce = () => {
  const { getData, postData, invalidateQuery } = useDataFetch();

  /**
   * Refresco manual del caché del dashboard.
   *
   * El caché se reconstruye solo cada 6 horas, así que después de una
   * liquidación hay que esperar al siguiente corte para ver los números. El
   * endpoint es GET, así que se dispara con un refetch en vez de una mutación.
   */
  const {
    refetch: refreshCache,
    isFetching: refreshingCache,
  } = getData(
    ['refresh-dashboard-cache'],
    '/charts/refresh-dashboard-cache',
    { enabled: false, retry: false }
  );

  /**
   * Recálculo de los puntos de red de todos los usuarios.
   *
   * Responde apenas encola: el barrido recorre la red completa y tarda
   * minutos, así que corre en una cola y no en la petición.
   */
  const { mutateAsync: recalcPoints } = postData(
    '/users/network-points/recalculate'
  );

  const handleRefreshCache = async () => {
    // Las dos cosas en paralelo: el caché del dashboard se reconstruye al
    // momento y los puntos de red quedan encolados.
    const [, pointsResult] = await Promise.all([
      refreshCache(),
      recalcPoints({}).catch((error) => ({ error })),
    ]);

    setPointsQueued(!pointsResult?.error);

    // Las tarjetas y las gráficas leen del caché: sin invalidar, la pantalla
    // sigue mostrando lo de antes aunque el caché ya se haya reconstruido.
    ['dashboard-cache', 'income', 'income-comparison', 'settlements-summary']
      .forEach((key) => invalidateQuery([key]));
  };

  // Estados para información financiera
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const {
    periodOptions,
    selectedInfluencerPeriod,
    setSelectedInfluencerPeriod,
    selectedLoyaltyPeriod,
    setSelectedLoyaltyPeriod
  } = useSettlementsSummary();


  const [filters, setFilters] = useState({
    city: '',
    department: ''
  });



  // Estados para información de gestión
  const [selectedCountry, setSelectedCountry] = useState('');
  const [managementDateRange, setManagementDateRange] = useState([null, null]);
  const [managementStartDate, managementEndDate] = managementDateRange;

  const [loading, setLoading] = useState(true);
  const [currencies, setCurrencies] = useState([]);
  const [totalIncome, setTotalIncome] = useState(0);
  const [pointsQueued, setPointsQueued] = useState(false);

  const { data: countriesData } = getData(
    ['countries-available'],
    '/product/countries-available/all'
  );

  // /users/loyalty-status/monthly-count quedó fuera: cuenta la cohorte que
  // VENCE en el mes, no los usuarios activos durante el mes. La tarjeta usa
  // ahora /users/active-monthly, que sí mide el traslape de la retención con
  // el mes. El endpoint viejo sigue existiendo por si otra pantalla lo usa.


  

  const {
    influencerData,
    loyaltyData,
    influencerLoading,
    loyaltyLoading,
  } = useSettlementData(
    selectedInfluencerPeriod || 'current',
    selectedLoyaltyPeriod || 'current',
    selectedCurrency
  );


  useEffect(() => {
    if (countriesData?.data?.countries?.length > 0) {
      // Configurar país por defecto para gestión
      if (!selectedCountry) {
        setSelectedCountry(countriesData.data.countries[0].id);
      }

      // Extraer currencies únicas
      const uniqueCurrencies = [...new Set(countriesData.data.countries.map(country => country.currency))];
      setCurrencies(uniqueCurrencies);

      // Configurar currency por defecto
      if (!selectedCurrency && uniqueCurrencies.length > 0) {
        setSelectedCurrency(uniqueCurrencies[0]);
      }

      // Configurar fechas por defecto para gestión (1 del mes actual hasta hoy)
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      if (!managementStartDate) {
        setManagementDateRange([firstDayOfMonth, now]);
      }

      setLoading(false);
    }
  }, [countriesData, selectedCountry, selectedCurrency, managementStartDate]);

  // Función para obtener ingresos por país
  const { data: incomeData, isLoading: incomeLoading } = getData(
    ['income', selectedCurrency],
    `/charts/income`,
    { enabled: !!selectedCurrency },
    {
      currency: selectedCurrency
    }
  );

  useEffect(() => {
    if (incomeData?.total || incomeData?.total === 0) {
      setTotalIncome(incomeData.total);
    }
  }, [incomeData]);

  // Comparación de ingresos contra el mismo tramo del mes anterior. Va aparte
  // de /charts/income porque ese endpoint sólo devuelve el total del período
  // actual y lo consumen otras pantallas que no deben cambiar de forma.
  const { data: incomeComparison } = getData(
    ['income-comparison', selectedCurrency,filters.department,filters.city],
    `/charts/income-comparison`,
    { enabled: !!selectedCurrency },
    { currency: selectedCurrency,province:filters.department,city:filters.city }
  );

  /**
   * Traza de la tarjeta "Ventas Netas".
   *
   * Compara el mes en curso contra el mes anterior COMPLETO, así que el
   * porcentaje arranca muy negativo y se va cerrando: sirve para ver cuánto
   * falta para igualar el mes pasado y en qué momento se superó.
   *
   * Va en un useEffect y no dentro del render para que salga una vez por
   * respuesta y no en cada repintado del componente.
   */
  useEffect(() => {
    const c = incomeComparison?.data;
    if (!c) return;

    // formatPeriodDate y no toLocaleString: los límites llegan como medianoche
    // UTC y el navegador, al leerlos en hora de Colombia, los corre cinco horas
    // y muestra el inicio el día anterior ("30/6" en vez de "1/7").
    const range = (p) => formatPeriodRange(p) ?? '—';
    const money = (n) => (n ?? 0).toLocaleString('es-CO');

    console.groupCollapsed(
      `📊 Ventas Netas (${c.currency}) · variación neta ${c.net_growth_percentage ?? '—'}%`,
    );
    console.table({
      Actual: {
        Rango: range(c.period),
        'Neto (sin comisiones)': money(c.net_total),
        'Total (con envío y comisión)': money(c.total),
        Órdenes: c.orders_count ?? 0,
      },
      Anterior: {
        Rango: range(c.previous_period),
        'Neto (sin comisiones)': money(c.previous_net_total),
        'Total (con envío y comisión)': money(c.previous_total),
        Órdenes: c.previous_orders_count ?? 0,
      },
    });
    console.log(
      `Variación neta: ${c.net_growth_percentage ?? '—'}%  ·  Variación total: ${c.growth_percentage ?? '—'}%`,
    );

    // Cuánto falta para igualar el mes pasado, que es la lectura de meta.
    const falta = (c.previous_net_total ?? 0) - (c.net_total ?? 0);
    console.log(
      falta > 0
        ? `Faltan ${money(falta)} para igualar el mes anterior completo.`
        : `Mes anterior superado por ${money(Math.abs(falta))}.`,
    );
    console.groupEnd();
  }, [incomeComparison]);

  // Afiliados nuevos de los últimos 3 meses contra los 3 anteriores.
  const { data: networkGrowthData } = getData(
    ['network-growth',filters.department,filters.city],
    `/users/network-growth`,
    {},
    { months: 3,province:filters.department,city:filters.city}
  );

  // Usuarios cuya retención cubre el mes en curso, contra el mes pasado.
  const { data: activeUsersData } = getData(
    ['active-users-monthly',filters.department,filters.city],
    `/users/active-monthly`,
    {},
    {province:filters.department,city:filters.city}
  );

  const clearFilters = () => {
    setFilters({
      period: 'month',
      from: new Date(),
      to: new Date(),
      city: '',
      department: ''
    });
  }


  

  // Variación contra el período cerrado anterior, que el caché del dashboard
  // calcula desde la última liquidación guardada (mes anterior para regalías,
  // semana anterior para bonos).
  //
  // Se deja pasar el null a propósito: significa que todavía no hay un corte
  // previo con el cual comparar, y la tarjeta lo dice en vez de inventar un 0%.
  const loyaltyGrowth =
    loyaltyData?.growth?.[selectedCurrency]?.value_percentage ?? null;
  const influencerGrowth =
    influencerData?.growth?.[selectedCurrency]?.value_percentage ?? null;

  /**
   * Fechas del corte comparado, para el tooltip.
   *
   * La tarjeta muestra "vs mes anterior", pero el backend compara contra la
   * última liquidación GUARDADA, que no siempre es la del período inmediato:
   * si un corte no se alcanzó a generar, la comparación salta al anterior.
   * Dejar las fechas a un hover permite notar ese salto sin llenar la tarjeta.
   */
  const buildPreviousTitle = (block) => {
    const period = block?.previous?.period;
    if (!period?.startFormatted) return null;
    return `Comparado contra el corte del ${period.startFormatted} al ${period.endFormatted}`;
  };

  const dashboardCards = cards.map(card => {
    switch (card.title) {
      case 'Regalías Totales':
        return {
          ...card,
          value: `${selectedCurrency} ${loyaltyData?.totalSettlementByCurrency?.[selectedCurrency]?.total_value?.toLocaleString() || 0
            }`,
          growth: loyaltyGrowth,
          growthLabel: card.growthLabel,
          growthTitle: buildPreviousTitle(loyaltyData),
        };

      case 'Bonos Totales':
        return {
          ...card,
          value: `${selectedCurrency} ${influencerData?.totalSettlementByCurrency?.[selectedCurrency]?.total_value?.toLocaleString() || 0
            }`,
          growth: influencerGrowth,
          growthLabel: card.growthLabel,
          growthTitle: buildPreviousTitle(influencerData),
        };

      case 'Ventas Netas': {
        const comparison = incomeComparison?.data;

        // Los dos períodos, con nombre de mes, para que se vea contra qué se
        // está comparando y cuánto fue el mes anterior.
        //
        // formatPeriodMonth y no toLocaleDateString: los límites llegan como
        // medianoche UTC y leerlos en hora local los corre al mes anterior.
        const currentMonth = formatPeriodMonth(comparison?.period?.start);

        // La comparación es contra el mes anterior COMPLETO, para leer la
        // tarjeta como avance hacia la meta: el porcentaje arranca muy negativo
        // y se pone en verde el día que se supera el mes pasado. El rango
        // exacto y el monto que falta van en el tooltip.
        const previousRange = comparison?.previous_period
          ? `${formatPeriodDate(comparison.previous_period.start)} - ${formatPeriodDate(comparison.previous_period.end)}`
          : null;

        const remaining =
          (comparison?.previous_net_total ?? 0) - (comparison?.net_total ?? 0);

        // La tarjeta dice "Sin comisiones", así que muestra `net_total` —la
        // suma de los subtotales— y no `total`, que incluye envío y comisión
        // de pasarela. El porcentaje va con su pareja `net_growth_percentage`
        // para que el valor y su variación midan exactamente lo mismo.
        const netIncome = comparison?.net_total;

        return {
          ...card,
          value: `${selectedCurrency} ${(netIncome ?? totalIncome ?? 0).toLocaleString()}`,
          subtitle: currentMonth
            ? `Sin comisiones`
            : card.subtitle,
          growth: comparison?.net_growth_percentage ?? null,
          growthLabel: card.growthLabel,
          growthTitle: previousRange
            ? `Mes anterior completo (${previousRange}): ${selectedCurrency} ${(comparison?.previous_net_total ?? 0).toLocaleString()}` +
              (remaining > 0
                ? ` · faltan ${selectedCurrency} ${remaining.toLocaleString()}`
                : ` · superado por ${selectedCurrency} ${Math.abs(remaining).toLocaleString()}`)
            : null,
        };
      }
      case 'Crecimiento de Red': {
        // El valor de esta tarjeta es el porcentaje mismo; el conteo de
        // afiliados va en el subtítulo para dar contexto.
        const pct = networkGrowthData?.data?.growth_percentage;
        const newUsers = networkGrowthData?.data?.new_users;

        return {
          ...card,
          value: pct === null || pct === undefined
            ? '—'
            : `${pct > 0 ? '+' : ''}${pct}%`,
          // El subtítulo se dejó en una sola línea. Con "· vs 3 meses
          // anteriores" pegado se partía en dos renglones y empujaba el valor
          // hacia abajo, así que esta tarjeta quedaba desalineada con las otras
          // cuatro. Esta tarjeta es `accentValue` (el número ES el indicador):
          // no lleva nada debajo del porcentaje, así que ese período no se
          // muestra en ningún lado — sólo queda el conteo de afiliados arriba.
          subtitle: newUsers !== undefined
            ? `${newUsers.toLocaleString()} afiliados nuevos`
            : card.subtitle,
          growth: null,
        };
      }

      case 'Usuarios Activos':
        return {
          ...card,
          value: `${activeUsersData?.data?.current_month?.toLocaleString() || 0}`,
          growth: activeUsersData?.data?.growth_percentage ?? null,
        };

      default:
        return card;
    }
  });


  // En móvil hay que elegir una cifra: Ventas Netas sube a tarjeta principal
  // con la esquina diagonal y las otras cuatro van en rejilla 2×2. Si la
  // métrica que manda para el negocio es otra, se cambia el título de aquí.
  const heroCard = dashboardCards.find((card) => card.title === 'Ventas Netas');
  const miniCards = dashboardCards.filter((card) => card !== heroCard);

  return (
    <Fragment>
      <PageHeader
        title="Tablero"
        crumbs={[{ label: 'General' }, { label: 'Tablero' }]}
      />

      <DashboardFilters
        filters={filters}
        setFilters={setFilters}
        countries={countriesData?.data?.countries ?? []}
        selectedCountry={selectedCountry}
        onCountryChange={setSelectedCountry}
        onClear={clearFilters}
      />

      {/*
        `repeat(auto-fit, minmax(215px, 1fr))`: las cinco tarjetas se
        reacomodan en varias filas antes de aplastar las cifras, que es lo
        que pasaba con las clases de ancho fijo de Bootstrap.
      */}
      <div className="ui-grid-kpis d-none d-lg-grid mb-4">
        {dashboardCards.map((card) => (
          <DashboardStatCard key={card.title} {...card} />
        ))}
      </div>

      <div className="d-lg-none mb-3">
        {heroCard && (
          <div className="mb-3">
            <DashboardStatCard {...heroCard} variant="hero" />
          </div>
        )}

        <div className="ui-grid-2">
          {miniCards.map((card) => (
            <DashboardStatCard
              key={card.title}
              {...card}
              title={card.shortTitle || card.title}
              variant="mini"
            />
          ))}
        </div>
      </div>

      <div className="ui-grid-charts mb-4">
        <SalesGrowthChart selectedCountry={selectedCountry} />
        <UsersComparisonChart />
      </div>

      <div className="ui-grid-thirds">
        <CriticalAlertsCard />
        <AutomaticInsightsCard />
        <TopNetworksCard
          province={filters.department}
          city={filters.city}
        />
      </div>

      <div className="text-center py-4">
        <button
          type="button"
          className="ui-link"
          style={{ fontSize: 12, color: '#8A91AE' }}
          disabled={refreshingCache}
          onClick={handleRefreshCache}
        >
          {refreshingCache
            ? 'Actualizando...'
            : 'Actualizar caché y puntos de red'}
        </button>

        {pointsQueued && !refreshingCache && (
          <div style={{ fontSize: 11, color: '#8A91AE', marginTop: 4 }}>
            Caché actualizado. Los puntos de red quedaron en cola: tardan
            unos minutos y no requieren dejar esta pantalla abierta.
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default Ecommerce;
